import { CheckCircle2, Package, Truck } from 'lucide-react'
import React, { useEffect } from 'react'

import PalletCard from './components/PalletCard'
import ScanForm from './components/ScanForm'
import { Business, useScanActions, useScanState } from './stores/scanStore'

import { HouseBillStatus } from '@/types/customs'

const BUSINESS_OPTIONS = [
  {
    key: Business.OUTBOUND,
    label: '出库扫描',
    description: '适用于仓库出库作业，逐个托盘完成核对',
    icon: Package,
    theme: 'blue' as const
  },
  {
    key: Business.DELIVERY,
    label: '发货扫描',
    description: '适用于成车发运，按托盘快速确认发货状态',
    icon: Truck,
    theme: 'orange' as const
  }
]

const OutboundScan: React.FC = () => {
  const { houseBill, activeBusiness } = useScanState()
  const { setActiveBusiness, clearHouseBill } = useScanActions()

  // 当切换 activeBusiness 时清空 houseBill
  useEffect(() => {
    if (houseBill) {
      clearHouseBill()
    }
  }, [activeBusiness])

  return (
    <div className="space-y-4 h-full overflow-auto">
      <div className="px-4">
        <div className="grid gap-3 sm:grid-cols-2">
          {BUSINESS_OPTIONS.map((option) => {
            const Icon = option.icon
            const isActive = activeBusiness === option.key
            const isDelivery = option.theme === 'orange'

            const activeCardClasses = isDelivery
              ? 'border-orange-500 bg-orange-50 shadow-sm'
              : 'border-blue-500 bg-blue-50 shadow-sm'
            const inactiveCardClasses = isDelivery
              ? 'border-gray-200 bg-white hover:border-orange-400 hover:bg-orange-50/50'
              : 'border-gray-200 bg-white hover:border-blue-400 hover:bg-blue-50/50'
            const titleClasses = isActive
              ? isDelivery
                ? 'text-orange-700'
                : 'text-blue-700'
              : 'text-gray-800'
            const descriptionClasses = isActive
              ? isDelivery
                ? 'text-orange-600'
                : 'text-blue-600'
              : 'text-gray-500'
            const iconWrapperClasses = isActive
              ? isDelivery
                ? 'border-orange-400 bg-white text-orange-600'
                : 'border-blue-400 bg-white text-blue-600'
              : isDelivery
              ? 'border-gray-200 bg-gray-50 text-gray-400 group-hover:border-orange-300 group-hover:text-orange-500'
              : 'border-gray-200 bg-gray-50 text-gray-400 group-hover:border-blue-300 group-hover:text-blue-500'
            const focusRingClass = isDelivery
              ? 'focus-visible:ring-orange-400'
              : 'focus-visible:ring-blue-400'

            return (
              <button
                key={option.key}
                onClick={() => setActiveBusiness(option.key)}
                className={`group flex w-full items-center justify-between rounded-xl border-2 p-4 text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 ${focusRingClass} focus-visible:ring-offset-2 ${
                  isActive ? activeCardClasses : inactiveCardClasses
                }`}
                type="button"
              >
                <div>
                  <div className={`text-lg font-semibold ${titleClasses}`}>
                    {option.label}
                  </div>
                  <p className={`mt-1 text-sm ${descriptionClasses}`}>
                    {option.description}
                  </p>
                </div>
                <div
                  className={`flex h-12 w-12 items-center justify-center rounded-full border-2 transition-colors ${iconWrapperClasses}`}
                >
                  <Icon className="h-6 w-6" />
                </div>
              </button>
            )
          })}
        </div>
      </div>

      <div className="px-4">
        <div className="grid gap-3 sm:grid-cols-2">
          {/* 左侧操作区 */}
          <ScanForm />

          {/* 右侧信息展示区 */}

          <div className="w-full space-y-2">
            {/* 完成提示 */}
            {houseBill && (
              <>
                {activeBusiness === Business.OUTBOUND &&
                  (houseBill.status === HouseBillStatus.PENDING_DELIVERY ||
                    houseBill.status ===
                      HouseBillStatus.PENDING_CONFIRMATION) && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-400 rounded-xl p-5 space-y-3">
                      <div className="flex items-center space-x-3">
                        <div className="bg-blue-500 rounded-full p-2">
                          <CheckCircle2 className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h4 className="font-bold text-blue-900 text-lg">
                            ✓ 出库阶段已完成
                          </h4>
                          <p className="text-blue-700 text-sm">
                            所有托盘已完成出库确认
                          </p>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-orange-200">
                        <p className="text-orange-800 text-sm">
                          本次出库备出单
                          <span className="font-bold">
                            {houseBill.houseBlNumber}
                          </span>
                          的确认已完成，共计
                          <span className="font-bold text-lg">
                            {houseBill.warehousePallets?.length || 0}
                          </span>
                          个托盘。
                        </p>
                      </div>
                    </div>
                  )}

                {activeBusiness === Business.DELIVERY &&
                  houseBill.status === HouseBillStatus.PENDING_CONFIRMATION && (
                    <div className="bg-orange-50 border-2 border-orange-400 rounded-xl p-5 space-y-3 shadow-md">
                      <div className="flex items-center space-x-3">
                        <div className="bg-orange-500 rounded-full p-2">
                          <CheckCircle2 className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h4 className="font-bold text-orange-900 text-lg">
                            ✓ 发货阶段已完成
                          </h4>
                          <p className="text-orange-700 text-sm">
                            所有托盘已完成发货确认
                          </p>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-orange-200">
                        <p className="text-orange-800 text-sm">
                          本次出库发出单
                          <span className="font-bold">
                            {houseBill.houseBlNumber}
                          </span>
                          的确认已完成，共计
                          <span className="font-bold text-lg">
                            {houseBill.warehousePallets?.length || 0}
                          </span>
                          个托盘。
                        </p>
                      </div>
                    </div>
                  )}
              </>
            )}

            {houseBill &&
              houseBill.warehousePallets.map((pallet) => {
                return <PalletCard key={pallet.id} pallet={pallet} />
              })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default OutboundScan
